import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { FormContainer, FormItem, NativeSelect, Spinner } from 'elmo-elements';

import ColourPicker from '@/components/Form/ColourPicker';
import CKFileUpload from '@/components/CKFileUpload/CKFileUpload';
import { LayoutSectionComponent } from '@/components/LayoutSection';
import currentFrameDataSelector from '@/modules/selectors/frame';
import { useGetElearningQuery, useGetFramesQuery } from '@/modules/services';
import { FrameDataType } from '@/modules/types/frame';
import { CkFinderResourceTypeEnum } from '@/modules/types/ckFinder';

import { ElearningParamType } from '../../../routes';
import { ContentFormType } from '../SlideForm';

import './index.scss';
import { MuteType } from '@/modules/types/elearning/muteType.enum';
import { getCkFileType, getCkFinderConfig } from '@/lib/util';

interface SlideSetupPropsType {
  frameData: FrameDataType;
  form: UseFormReturn<ContentFormType, object>;
}

type FieldNameType =
  | 'elearningId'
  | 'id'
  | 'bgVideoTitle'
  | 'frameMetadata'
  | 'frameMetadata.backgroundColor'
  | 'frameMetadata.bgImage'
  | 'frameMetadata.mute'
  | 'frameMetadata.swipeNavigation'
  | 'frameMetadata.position'
  | 'frameMetadata.disabled'
  | 'frameMetadata.sound';

const muteTypes = [
  { label: 'No', value: MuteType.NO },
  { label: 'Yes', value: MuteType.YES },
  { label: 'Hidden', value: MuteType.HIDDEN },
];

const slideSetupSwipeNavigation = [
  { label: 'Enabled', value: '1' },
  { label: 'Disabled', value: '0' },
];

const SlideSetup = ({ form }: SlideSetupPropsType): React.ReactElement => {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
    getValues,
  } = form;
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const elearning = useGetElearningQuery(elearningId);
  const frames = useGetFramesQuery({ elearningId });

  const currentFrame = currentFrameDataSelector({
    elearningData: elearning.data,
    frameData: frames.data,
    currentFrameId: frameId,
  });

  const handleFieldChange = (e: any, fieldName: FieldNameType) => {
    const value = e.target ? e.target.value : e;
    setValue(fieldName, value);
  };

  const handleImageUpload = (currentFileName: string) => {
    setValue('frameMetadata.bgImage', currentFileName);
  };

  const handleSoundUpload = (currentFileName: string) => {
    setValue('frameMetadata.sound', currentFileName);
  };

  if (!frameId || !currentFrame) return <div />;
  if (frames.isLoading) return <Spinner />;

  return (
    <FormContainer maxColumns={2}>
      <FormItem
        label="Background Colour*"
        size="sm"
        message={errors.frameMetadata?.backgroundColor?.message}
        status={errors.frameMetadata?.backgroundColor ? 'error' : 'default'}
      >
        <ColourPicker
          id="SlideFormBGColor"
          defaultColor={watch('frameMetadata.backgroundColor') ?? ''}
          handleChange={(e: any) =>
            handleFieldChange(e, 'frameMetadata.backgroundColor')
          }
          register={register}
          registerName="frameMetadata.backgroundColor"
          watch={watch}
          isFloat={true}
          placement="bottom"
          key={`SlideFormBGColor-${currentFrame.id}`}
        />
      </FormItem>
      <FormItem
        label="Background Image"
        size="sm"
        className="elmo-formitem__single-file"
        message={errors.frameMetadata?.bgImage?.message}
        status={errors.frameMetadata?.bgImage ? 'error' : 'default'}
      >
        <CKFileUpload
          connectorDetails={getCkFinderConfig({
            type: getCkFileType([CkFinderResourceTypeEnum.IMAGES]),
            tenantId: elearning?.currentData?.tenantId ?? '',
            entityId: elearningId,
            folderName: 'elearnings',
          })}
          defaultFileName={getValues('frameMetadata.bgImage') ?? ''}
          fileType="images"
          handlerFunction={{ onFileChoose: handleImageUpload }}
          register={register}
          registerName="frameMetadata.bgImage"
        />
      </FormItem>
      <FormItem label="Mute" size="sm">
        <NativeSelect
          id="SlideFormMute"
          {...register('frameMetadata.mute', {})}
          onChange={(e) => handleFieldChange(e, 'frameMetadata.mute')}
          value={watch('frameMetadata.mute') || 0}
        >
          <option value="" disabled hidden>
            Mute
          </option>
          {muteTypes.map((mute, index) => (
            <NativeSelect.Option
              label={mute.label}
              value={mute.value}
              key={`mute-${index + 1}`}
            />
          ))}
        </NativeSelect>
      </FormItem>
      <FormItem
        label="Voice Over Sound"
        size="sm"
        className="elmo-formitem__single-file"
        message={errors.frameMetadata?.sound?.message}
        status={errors.frameMetadata?.sound ? 'error' : 'default'}
      >
        <CKFileUpload
          connectorDetails={getCkFinderConfig({
            type: getCkFileType([CkFinderResourceTypeEnum.SOUNDS]),
            tenantId: elearning?.currentData?.tenantId ?? '',
            entityId: elearningId,
            folderName: 'elearnings',
          })}
          defaultFileName={getValues('frameMetadata.sound') ?? ''}
          fileType="sounds"
          handlerFunction={{ onFileChoose: handleSoundUpload }}
          register={register}
          registerName="frameMetadata.sound"
        />
      </FormItem>
      <FormItem label="Swipe Navigation" size="sm">
        <NativeSelect
          id="SlideFormSwipeNavigation"
          {...register('frameMetadata.swipeNavigation', {})}
          onChange={(e) =>
            handleFieldChange(e, 'frameMetadata.swipeNavigation')
          }
          value={watch('frameMetadata.swipeNavigation') || 0}
        >
          <option value="" disabled hidden>
            Swipe Navigation
          </option>
          {slideSetupSwipeNavigation.map((swipeNavigation, index) => (
            <NativeSelect.Option
              label={swipeNavigation.label}
              value={swipeNavigation.value}
              key={`swipeNavigation-${index + 1}`}
            />
          ))}
        </NativeSelect>
      </FormItem>
    </FormContainer>
  );
};

const SlideSetupComponent = ({
  frameData,
  form,
}: SlideSetupPropsType): React.ReactElement => (
  <LayoutSectionComponent
    heading="Slide Setup"
    className="slide-setup-layout-section"
    content={<SlideSetup frameData={frameData} form={form} />}
    isCollapsible={true}
  />
);

export default SlideSetupComponent;
